import React from 'react';
import { Link } from 'react-router-dom';
import i18n from '../../i18n';

/**
 * Consistent chrome around every chart: title, optional action link,
 * loading skeleton, empty and error states.
 *
 * Content height grows with children so legends are never clipped.
 * Empty states stay compact so grids of empty panels don't look broken.
 */
export default function ChartPanel({
  title,
  subtitle,
  actionTo,
  actionLabel,
  loading = false,
  empty = false,
  emptyMessage = i18n.t('charts.noDataYet', { defaultValue: 'No data to chart yet' }),
  error = null,
  /** Loading skeleton height */
  height = 200,
  /** Compact empty/error placeholder height */
  emptyHeight = 96,
  children,
  className = '',
}) {
  const placeholderH = empty || error ? emptyHeight : height;

  return (
    <section
      className={`flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}
      aria-busy={loading || undefined}
    >
      {(title || actionTo) && (
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {title ? <h3 className="text-sm font-semibold text-slate-900">{title}</h3> : null}
            {subtitle ? <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
          </div>
          {actionTo && actionLabel ? (
            <Link
              to={actionTo}
              className="shrink-0 text-xs font-semibold text-primary-700 hover:underline"
            >
              {actionLabel}
            </Link>
          ) : null}
        </div>
      )}

      {error ? (
        <div
          className="flex items-center justify-center rounded-xl border border-amber-200 bg-amber-50 px-3 py-4 text-center text-xs text-amber-900"
          style={{ minHeight: placeholderH }}
          role="status"
        >
          {error}
        </div>
      ) : loading ? (
        <div
          className="animate-pulse rounded-xl bg-slate-100"
          style={{ minHeight: height }}
          aria-hidden
        />
      ) : empty || children == null || children === false ? (
        <div
          className="flex items-center justify-center rounded-xl bg-slate-50 px-3 py-4 text-center text-xs text-slate-500"
          style={{ minHeight: emptyHeight }}
          role="status"
        >
          {emptyMessage}
        </div>
      ) : (
        <div className="min-w-0">{children}</div>
      )}
    </section>
  );
}
